/* Master Item Catalog — Procurement foundation page.
 *
 * The dictionary that powers RFQ comparison: every item carries an
 * ``item_code``, canonical description, UOM, category and optional
 * aliases that absorb supplier-side naming variance.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Search, Tag, Package, X, Save, Trash2, DollarSign, Truck } from 'lucide-react';


const VALID_UOMS = [
  'm', 'cm', 'mm', 'km', 'm2', 'm3', 'l',
  'kg', 'g', 'ton', 'ea', 'pc', 'box', 'set',
  'pack', 'pair', 'hr', 'day', 'month', 'service',
  'drum', 'gallon',
];


const ItemMaster = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [supplierPanel, setSupplierPanel] = useState(null);  // {item, data}
  const [form, setForm] = useState({
    item_code: '', description: '', description_arabic: '',
    category: '', sub_category: '', uom: 'ea',
    default_unit_price: '', aliases: '',
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '200');
      if (search) params.set('search', search);
      const r = await axiosInstance.get(`/item-master?${params.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (_) {
      toast.error('Failed to load catalog');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { refresh(); }, [refresh]);

  const openSupplierPanel = async (item) => {
    try {
      const r = await axiosInstance.get(
        `/item-master/${encodeURIComponent(item.item_code)}/supplier-prices`,
      );
      setSupplierPanel({ item, data: r.data });
    } catch (_) {
      toast.error('Failed to load supplier prices');
    }
  };

  const onCreate = async () => {
    try {
      const payload = {
        ...form,
        default_unit_price: form.default_unit_price === ''
          ? null : parseFloat(form.default_unit_price),
        aliases: form.aliases
          ? form.aliases.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
      };
      // strip empty item_code so the backend auto-generates
      if (!payload.item_code) delete payload.item_code;
      const r = await axiosInstance.post('/item-master', payload);
      toast.success(`Created ${r.data?.item_code}`);
      setShowCreate(false);
      setForm({ item_code: '', description: '', description_arabic: '',
                category: '', sub_category: '', uom: 'ea',
                default_unit_price: '', aliases: '' });
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        (detail && (detail.error || detail.message)) ||
        'Failed to create item',
      );
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="item-master-page">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6" />
            Master Item Catalog
            <span className="text-sm font-mono px-2 py-0.5 rounded bg-gray-900 text-white"
                  data-testid="item-master-total-count">
              {total}
            </span>
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            The canonical dictionary for procurement. Every supplier
            quotation, RFQ, and PO resolves to a row here.
          </p>
        </div>
        <button
          data-testid="item-master-new-button"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">
          <Plus className="w-4 h-4" /> New Item
        </button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="item-master-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by description, category, or alias…"
            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded text-sm focus:border-indigo-500 focus:outline-none"
          />
        </div>
      </div>

      {loading && <div className="text-sm text-gray-500" data-testid="item-master-loading">Loading…</div>}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Item Code</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">UOM</th>
              <th className="text-right px-3 py-2">Last Purchase</th>
              <th className="text-right px-3 py-2">Avg Price</th>
              <th className="text-right px-3 py-2">Buys</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan={8} className="text-center text-gray-500 py-8" data-testid="item-master-empty">
                No items yet. Click "New Item" to seed the catalog.
              </td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className="border-t border-gray-100 hover:bg-indigo-50/30"
                  data-testid={`item-master-row-${it.item_code}`}>
                <td className="px-3 py-2 font-mono text-xs">{it.item_code}</td>
                <td className="px-3 py-2">
                  <div className="font-medium">{it.description}</div>
                  {it.description_arabic && (
                    <div className="text-xs text-gray-500" dir="rtl">{it.description_arabic}</div>
                  )}
                  {(it.aliases || []).length > 0 && (
                    <div className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      {(it.aliases || []).slice(0, 2).join(' · ')}
                      {it.aliases.length > 2 && <span>+{it.aliases.length - 2}</span>}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{it.category}{it.sub_category && <span className="text-gray-400"> / {it.sub_category}</span>}</td>
                <td className="px-3 py-2 font-mono text-xs">{it.uom}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {it.last_purchase_price != null ? it.last_purchase_price.toLocaleString() : '—'}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {it.average_purchase_price != null ? it.average_purchase_price.toLocaleString() : '—'}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">{it.purchase_count || 0}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => openSupplierPanel(it)}
                    data-testid={`item-suppliers-button-${it.item_code}`}
                    className="text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-indigo-50"
                    title="View supplier prices">
                    <DollarSign className="w-3.5 h-3.5" /> Suppliers
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Supplier Prices Modal — multi-supplier view for one item */}
      {supplierPanel && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4"
             data-testid="item-supplier-prices-modal"
             onClick={() => setSupplierPanel(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="font-semibold flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-indigo-600" />
                  Supplier Prices · <span className="font-mono">{supplierPanel.item.item_code}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{supplierPanel.item.description}</div>
              </div>
              <button onClick={() => setSupplierPanel(null)}
                      data-testid="item-supplier-prices-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            {supplierPanel.data?.stats?.cheapest != null && (
              <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-200 text-xs flex gap-4 flex-wrap">
                <span><strong>{supplierPanel.data.supplier_count}</strong> suppliers</span>
                <span>Cheapest: <strong className="text-emerald-700">{supplierPanel.data.stats.cheapest}</strong></span>
                <span>Median: <strong>{supplierPanel.data.stats.median}</strong></span>
                <span>Most expensive: <strong className="text-rose-700">{supplierPanel.data.stats.most_expensive}</strong></span>
                <span>Spread: <strong>{supplierPanel.data.stats.spread_pct}%</strong></span>
              </div>
            )}
            <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2">Supplier</th>
                    <th className="text-right px-3 py-2">Unit Price</th>
                    <th className="text-right px-3 py-2">Lead</th>
                    <th className="text-left px-3 py-2">Source</th>
                    <th className="text-left px-3 py-2">As of</th>
                  </tr>
                </thead>
                <tbody>
                  {(supplierPanel.data?.rows || []).length === 0 ? (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-gray-500">
                      No supplier prices recorded yet. Awards & GRNs auto-feed this list.
                    </td></tr>
                  ) : (supplierPanel.data.rows.map((r, i) => (
                    <tr key={r.id} className="border-t border-gray-100"
                        data-testid={`item-supplier-price-row-${i}`}>
                      <td className="px-3 py-2 text-sm">
                        <div className="inline-flex items-center gap-1">
                          <Truck className="w-3 h-3 text-gray-400" /> {r.supplier_name}
                          {i === 0 && (
                            <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">★ CHEAPEST</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-indigo-700">
                        {Number(r.unit_price).toLocaleString()} {r.currency || 'SAR'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {r.lead_time_days != null ? `${r.lead_time_days}d` : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                          r.source === 'rfq_award' ? 'bg-emerald-100 text-emerald-800' :
                          r.source === 'grn_posted' ? 'bg-violet-100 text-violet-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>{r.source}</span>
                        {r.source_ref_no && (
                          <span className="ml-1 text-[10px] font-mono text-gray-500">{r.source_ref_no}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">{r.created_at?.slice(0, 10)}</td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" data-testid="item-master-create-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <div className="font-semibold flex items-center gap-2">
                <Plus className="w-4 h-4" /> New Catalog Item
              </div>
              <button onClick={() => setShowCreate(false)} data-testid="item-master-create-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3 text-sm">
              <label className="col-span-2">
                <span className="text-xs text-gray-600">Item Code (optional — auto-generated if blank)</span>
                <input data-testid="item-master-input-code" value={form.item_code}
                       onChange={(e) => setForm({...form, item_code: e.target.value})}
                       className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded font-mono text-xs" />
              </label>
              <label>
                <span className="text-xs text-gray-600">Description *</span>
                <input data-testid="item-master-input-desc" value={form.description}
                       onChange={(e) => setForm({...form, description: e.target.value})}
                       className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded" />
              </label>
              <label>
                <span className="text-xs text-gray-600">الوصف العربي</span>
                <input data-testid="item-master-input-desc-ar" value={form.description_arabic} dir="rtl"
                       onChange={(e) => setForm({...form, description_arabic: e.target.value})}
                       className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded" />
              </label>
              <label>
                <span className="text-xs text-gray-600">Category *</span>
                <input data-testid="item-master-input-category" value={form.category}
                       onChange={(e) => setForm({...form, category: e.target.value})}
                       className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded" />
              </label>
              <label>
                <span className="text-xs text-gray-600">Sub-category</span>
                <input value={form.sub_category}
                       onChange={(e) => setForm({...form, sub_category: e.target.value})}
                       className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded" />
              </label>
              <label>
                <span className="text-xs text-gray-600">UOM *</span>
                <select data-testid="item-master-input-uom" value={form.uom}
                        onChange={(e) => setForm({...form, uom: e.target.value})}
                        className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded">
                  {VALID_UOMS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </label>
              <label>
                <span className="text-xs text-gray-600">Default Unit Price (SAR)</span>
                <input type="number" step="0.01" value={form.default_unit_price}
                       onChange={(e) => setForm({...form, default_unit_price: e.target.value})}
                       className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded font-mono" />
              </label>
              <label className="col-span-2">
                <span className="text-xs text-gray-600">Aliases (comma-separated; help match supplier-side names)</span>
                <input data-testid="item-master-input-aliases" value={form.aliases}
                       onChange={(e) => setForm({...form, aliases: e.target.value})}
                       placeholder="10MM Cable, Cable 10mm, كابل 10"
                       className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded" />
              </label>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setShowCreate(false)}
                      data-testid="item-master-create-cancel"
                      className="px-3 py-1.5 rounded border border-gray-300 text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={onCreate}
                      data-testid="item-master-create-submit"
                      disabled={!form.description || !form.category}
                      className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold inline-flex items-center gap-1">
                <Save className="w-4 h-4" /> Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemMaster;
